import * as React from "react";
import PropTypes from "prop-types";
import { Global } from "@emotion/react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { grey } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import ChatbotPage from "../pages/ChatBot/ChatbotPage";

const drawerBleeding = 0;

// Styling for the Root container
const Root = styled("div")(({ theme }) => ({
  height: "100%",
  backgroundColor: grey[100],
  ...theme.applyStyles("dark", {
    backgroundColor: theme.palette.background.default,
  }),
}));

// Styling for the top drawer section (Puller and Title)
const StyledBox = styled("div")(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.applyStyles("dark", {
    backgroundColor: grey[800],
  }),
  display: "flex",
  flexDirection: "column",
  padding: 16,
}));

// Styling for the puller element (the draggable handle to open/close the drawer)
const Puller = styled("div")(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: grey[900],
  }),
}));

// Drawer content container with restricted height and scroll
const DrawerContent = styled("div")({
  height: "calc(100% - 40px)", // Adjust height based on the header (Puller and title)
  overflowY: "auto",
});

// The SwipeableEdgeDrawer component
function SwipeableEdgeDrawer(props) {
  const { window, text, image, open, setOpen, timestamp, modelConfig } = props;

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Root>
      <CssBaseline />
      <Global
        styles={{
          ".MuiDrawer-root > .MuiPaper-root": {
            height: `calc(75% - ${drawerBleeding}px)`, // Adjust the drawer height
            overflow: "visible",
            color: "#1e1e1e",
          },
        }}
      />
      <SwipeableDrawer
        container={container}
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {/* Header with Puller */}
        <StyledBox
          sx={{
            position: "absolute",
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: "visible",
            right: 0,
            left: 0,
          }}
        >
          <Puller />
          <Typography sx={{ p: 1, color: "text.secondary" }}>
            View Results
          </Typography>
        </StyledBox>

        {/* Drawer Content */}
        <DrawerContent>
          <ChatbotPage
            setOpen={setOpen}
            showCloseIcon={true}
            text={text}
            image={image}
            cameraConfig={modelConfig}
            showHeader={false}
          />
        </DrawerContent>
      </SwipeableDrawer>
    </Root>
  );
}

SwipeableEdgeDrawer.propTypes = {
  window: PropTypes.func,
};

export default SwipeableEdgeDrawer;
